/**
 * Container Validation Tab
 *
 * Displays cross-check results and single-document validation issues
 * for a container. Read-only — triggering new validations is done via
 * the "Evaluate Documents" button in the container header.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    MinusCircle,
    File02,
    ArrowRight,
    ChevronDown,
    ChevronUp,
    Lock01,
    SearchRefraction,
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import {
    useContainerValidation,
    type CrossCheckResultDetail,
    type SingleDocIssueDetail,
} from '@/hooks/api/use-container-validation';
import { ChangeIssueStatusModal } from './modals/change-issue-status-modal';
import type { ContainerIssue } from '@/hooks/api/use-container-issues';

interface ContainerValidationTabProps {
    containerId: string;
    containerNumber: string;
}

type FilterType = 'all' | 'cross_check' | 'single_doc';

// ============================================================================
// Helpers
// ============================================================================

const formatTimeAgo = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('it-IT');
};

const getSeverityColor = (severity: string | null): 'error' | 'warning' | 'brand' | 'gray' => {
    switch (severity?.toLowerCase()) {
        case 'critical':
        case 'error':
            return 'error';
        case 'high':
        case 'warning':
            return 'warning';
        case 'medium':
            return 'brand';
        case 'low':
        case 'info':
        default:
            return 'gray';
    }
};

const getStatusColor = (status: string): 'error' | 'warning' | 'success' | 'brand' | 'gray' => {
    switch (status.toLowerCase()) {
        case 'open':
            return 'error';
        case 'acknowledged':
        case 'in_progress':
            return 'brand';
        case 'pending_info':
        case 'waiting_on_external':
            return 'warning';
        case 'resolved':
            return 'success';
        case 'false_positive':
        case 'wont_fix':
            return 'gray';
        default:
            return 'gray';
    }
};

const formatLabel = (str: string): string =>
    str.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());


/** Deduplicate missing_document results by message, keeping only the most recent per unique message. */
function dedupMissingDocResults(results: CrossCheckResultDetail[]): CrossCheckResultDetail[] {
    const seen = new Map<string, CrossCheckResultDetail>();
    for (const r of results) {
        if (r.status === 'missing_document') {
            const existing = seen.get(r.message);
            if (!existing || r.executed_at > existing.executed_at) {
                seen.set(r.message, r);
            }
        }
    }
    return results.filter((r) => r.status !== 'missing_document' || seen.get(r.message) === r);
}

const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
        case 'success':
            return <CheckCircle className="size-5 text-success-600 shrink-0" />;
        case 'failed':
        case 'error':
            return <XCircle className="size-5 text-error-600 shrink-0" />;
        case 'skipped':
            return <MinusCircle className="size-5 text-tertiary shrink-0" />;
        case 'missing_document':
            return <AlertCircle className="size-5 text-warning-600 shrink-0" />;
        default:
            return <AlertCircle className="size-5 text-tertiary shrink-0" />;
    }
};

// ============================================================================
// Sub-components
// ============================================================================

function SummaryBar({
    passed,
    failed,
    missingDocs,
    skipped,
    singleDocOpen,
    lastRunAt,
}: {
    passed: number;
    failed: number;
    missingDocs: number;
    skipped: number;
    singleDocOpen: number;
    lastRunAt: string | null;
}) {
    const executed = passed + failed;
    return (
        <div className="rounded-lg bg-secondary/30 ring-1 ring-secondary px-4 py-3 space-y-2">
            {/* Row 1: Cross-Check results */}
            <div className="flex flex-wrap items-center gap-4">
                <span className="text-xs font-semibold text-tertiary uppercase tracking-wide">Cross-Check</span>
                <div className="flex items-center gap-1.5">
                    <CheckCircle className="size-4 text-success-600" />
                    <span className="text-sm font-medium text-primary">{passed}/{executed}</span>
                    <span className="text-sm text-tertiary">passed</span>
                </div>
                {failed > 0 && (
                    <div className="flex items-center gap-1.5">
                        <XCircle className="size-4 text-error-600" />
                        <span className="text-sm font-medium text-primary">{failed}</span>
                        <span className="text-sm text-tertiary">failed</span>
                    </div>
                )}
                {missingDocs > 0 && (
                    <div className="flex items-center gap-1.5">
                        <AlertCircle className="size-4 text-warning-600" />
                        <span className="text-sm font-medium text-primary">{missingDocs}</span>
                        <span className="text-sm text-tertiary">missing docs</span>
                    </div>
                )}
                {skipped > 0 && (
                    <div className="flex items-center gap-1.5">
                        <MinusCircle className="size-4 text-tertiary" />
                        <span className="text-sm font-medium text-primary">{skipped}</span>
                        <span className="text-sm text-tertiary">skipped</span>
                    </div>
                )}
                {lastRunAt && (
                    <div className="ml-auto text-xs text-tertiary">
                        Last run: {formatTimeAgo(lastRunAt)}
                    </div>
                )}
            </div>
            {/* Row 2: Single-doc issues */}
            {singleDocOpen > 0 && (
                <div className="flex items-center gap-4 pt-1 border-t border-secondary/50">
                    <span className="text-xs font-semibold text-tertiary uppercase tracking-wide">Single Doc</span>
                    <div className="flex items-center gap-1.5">
                        <AlertCircle className="size-4 text-warning-600" />
                        <span className="text-sm font-medium text-primary">{singleDocOpen}</span>
                        <span className="text-sm text-tertiary">issue{singleDocOpen !== 1 ? 's' : ''} open</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function CrossCheckCard({ result }: { result: CrossCheckResultDetail }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            className="rounded-lg ring-1 ring-secondary bg-primary overflow-hidden cursor-pointer hover:ring-brand-300 transition-all"
            onClick={() => setExpanded(!expanded)}
        >
            <div className="p-4 space-y-3">
                {/* Top row: status + badges */}
                <div className="flex items-center gap-2 flex-wrap">
                    <StatusIcon status={result.status} />
                    <Badge color="brand" size="sm">Cross-Check</Badge>
                    {result.severity && (
                        <Badge color={getSeverityColor(result.severity)} size="sm">
                            {formatLabel(result.severity)}
                        </Badge>
                    )}
                    {result.is_blocking && (
                        <Badge color="error" size="sm">
                            <Lock01 className="size-3 mr-0.5" />
                            Blocking
                        </Badge>
                    )}
                    <div className="ml-auto flex items-center gap-1 text-xs text-tertiary">
                        {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </div>
                </div>

                {/* Rule name + comparison type */}
                <div>
                    <p className="text-sm font-medium text-primary">
                        {result.rule_name || result.rule_code || 'Unknown rule'}
                    </p>
                    {result.comparison_type && (
                        <p className="text-xs text-tertiary">
                            Comparison: {formatLabel(result.comparison_type)}
                        </p>
                    )}
                </div>

                {/* Document pair */}
                {(result.source_document_name || result.target_document_name) && (
                    <div className="flex items-center gap-2 text-sm text-secondary">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <File02 className="size-4 shrink-0 text-tertiary" />
                            <span className="truncate">{result.source_document_name || 'N/A'}</span>
                            {result.source_document_type && (
                                <Badge color="gray" size="sm">{formatLabel(result.source_document_type)}</Badge>
                            )}
                        </div>
                        <ArrowRight className="size-4 shrink-0 text-tertiary" />
                        <div className="flex items-center gap-1.5 min-w-0">
                            <File02 className="size-4 shrink-0 text-tertiary" />
                            <span className="truncate">{result.target_document_name || 'N/A'}</span>
                            {result.target_document_type && (
                                <Badge color="gray" size="sm">{formatLabel(result.target_document_type)}</Badge>
                            )}
                        </div>
                    </div>
                )}

                {/* Values (when both present and result is failed) */}
                {result.status === 'failed' && result.source_value && result.target_value && (
                    <div className="flex gap-4 text-xs">
                        <div className="flex-1 rounded bg-error-50 px-2.5 py-1.5">
                            <span className="text-error-700 font-mono">{result.source_value}</span>
                        </div>
                        <div className="flex-1 rounded bg-error-50 px-2.5 py-1.5">
                            <span className="text-error-700 font-mono">{result.target_value}</span>
                        </div>
                    </div>
                )}

                {/* Message */}
                {!expanded && result.message && (
                    <p className="text-xs text-tertiary line-clamp-2">{result.message}</p>
                )}
            </div>

            {/* Expanded details */}
            {expanded && (
                <div className="border-t border-secondary px-4 py-3 bg-secondary/20 space-y-2">
                    {result.message && (
                        <div>
                            <span className="text-xs font-medium text-secondary">Message</span>
                            <p className="text-sm text-primary whitespace-pre-wrap">{result.message}</p>
                        </div>
                    )}
                    {result.source_value && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <span className="text-xs font-medium text-secondary">Source Value</span>
                                <p className="text-sm font-mono text-primary break-all">{result.source_value}</p>
                            </div>
                            <div>
                                <span className="text-xs font-medium text-secondary">Target Value</span>
                                <p className="text-sm font-mono text-primary break-all">{result.target_value || '-'}</p>
                            </div>
                        </div>
                    )}
                    {result.confidence !== null && result.confidence !== undefined && (
                        <div>
                            <span className="text-xs font-medium text-secondary">Confidence</span>
                            <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-brand-600 transition-all"
                                        style={{ width: `${Math.round(result.confidence * 100)}%` }}
                                    />
                                </div>
                                <span className="text-xs text-primary font-medium">
                                    {Math.round(result.confidence * 100)}%
                                </span>
                            </div>
                        </div>
                    )}
                    {result.comparison_type && (
                        <div>
                            <span className="text-xs font-medium text-secondary">Comparison Type</span>
                            <p className="text-sm text-primary">{formatLabel(result.comparison_type)}</p>
                        </div>
                    )}
                    {result.details && Object.keys(result.details).length > 0 && (
                        <div>
                            <span className="text-xs font-medium text-secondary">Details</span>
                            <div className="mt-1 space-y-1">
                                {Object.entries(result.details).map(([key, val]) => (
                                    <div key={key} className="flex gap-2 text-xs">
                                        <span className="text-tertiary font-mono">{key}:</span>
                                        <span className="text-primary font-mono break-all">
                                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="text-xs text-tertiary pt-1">
                        Executed: {new Date(result.executed_at).toLocaleString('it-IT')}
                    </div>
                </div>
            )}
        </div>
    );
}

function SingleDocIssueCard({
    issue,
    containerId,
    containerNumber,
}: {
    issue: SingleDocIssueDetail;
    containerId: string;
    containerNumber: string;
}) {
    const [expanded, setExpanded] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);

    // Adapt to ContainerIssue shape for the modal
    const asContainerIssue: ContainerIssue = {
        issue_id: issue.issue_id,
        issue_type: issue.issue_type,
        issue_source: null,
        title: issue.issue_description,
        description: null,
        severity: issue.severity as ContainerIssue['severity'],
        priority: null,
        resolution_status: issue.resolution_status,
        blocks_shipment: false,
        risk_contribution: null,
        issue_metadata: null,
        created_at: issue.detected_at,
    };

    return (
        <>
            <div
                className="rounded-lg ring-1 ring-secondary bg-primary overflow-hidden cursor-pointer hover:ring-brand-300 transition-all"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="p-4 space-y-3">
                    {/* Top row */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusIcon status={issue.resolution_status === 'resolved' ? 'success' : 'failed'} />
                        <Badge color={getSeverityColor(issue.severity)} size="sm">
                            {formatLabel(issue.severity)}
                        </Badge>
                        <Badge color={getStatusColor(issue.resolution_status)} size="sm">
                            {formatLabel(issue.resolution_status)}
                        </Badge>
                        <div className="ml-auto flex items-center gap-1 text-xs text-tertiary">
                            {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                        </div>
                    </div>

                    {/* Document name */}
                    {issue.document_name && (
                        <div className="flex items-center gap-1.5 text-sm">
                            <File02 className="size-4 text-tertiary" />
                            <span className="text-primary">{issue.document_name}</span>
                            {issue.document_type && (
                                <Badge color="gray" size="sm">{formatLabel(issue.document_type)}</Badge>
                            )}
                        </div>
                    )}

                    {/* Issue info */}
                    <div className="text-sm text-secondary">
                        <span className="font-medium">{formatLabel(issue.issue_type)}</span>
                        {issue.affected_field && (
                            <span className="text-tertiary"> — <code className="text-xs bg-secondary px-1 py-0.5 rounded">{issue.affected_field}</code></span>
                        )}
                    </div>

                    {!expanded && (
                        <p className="text-xs text-tertiary line-clamp-2">{issue.issue_description}</p>
                    )}
                </div>

                {/* Expanded */}
                {expanded && (
                    <div className="border-t border-secondary px-4 py-3 bg-secondary/20 space-y-2" onClick={(e) => e.stopPropagation()}>
                        <div>
                            <span className="text-xs font-medium text-secondary">Description</span>
                            <p className="text-sm text-primary whitespace-pre-wrap">{issue.issue_description}</p>
                        </div>
                        {issue.expected_value && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <span className="text-xs font-medium text-secondary">Expected</span>
                                    <p className="text-sm font-mono text-primary">{issue.expected_value}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-medium text-secondary">Actual</span>
                                    <p className="text-sm font-mono text-primary">{issue.actual_value || '-'}</p>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-4 text-xs text-tertiary">
                            <span>Detected: {formatTimeAgo(issue.detected_at)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            {issue.document_id && (
                                <a
                                    href={`/shipper/containers/${containerId}/documents/${issue.document_id}?mode=view`}
                                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Document
                                </a>
                            )}
                            <button
                                className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                                onClick={() => setShowStatusModal(true)}
                            >
                                Change Status
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showStatusModal && (
                <ChangeIssueStatusModal
                    isOpen={showStatusModal}
                    onClose={() => setShowStatusModal(false)}
                    issue={asContainerIssue}
                    containerId={containerId}
                    containerNumber={containerNumber}
                />
            )}
        </>
    );
}

// ============================================================================
// Main component
// ============================================================================

export function ContainerValidationTab({ containerId, containerNumber }: ContainerValidationTabProps) {
    const { t } = useTranslation('pages:containers');
    const [filter, setFilter] = useState<FilterType>('all');
    const { data, isLoading } = useContainerValidation(containerId);

    // --- Loading state ---
    if (isLoading) {
        return (
            <div className="space-y-4 animate-pulse">
                <div className="h-12 rounded-lg bg-secondary" />
                <div className="flex gap-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-8 w-24 rounded-full bg-secondary" />
                    ))}
                </div>
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-28 rounded-lg bg-secondary" />
                ))}
            </div>
        );
    }

    // --- Empty state ---
    if (!data || data.summary.total === 0) {
        return (
            <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                <SearchRefraction className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">
                    {t('detail.validation.noResults', 'No validation results yet')}
                </h3>
                <p className="text-tertiary">
                    {t(
                        'detail.validation.noResultsMessage',
                        'Use "Evaluate Documents" to run cross-checks and validation.'
                    )}
                </p>
            </div>
        );
    }

    // --- Dedup missing_document results by message (keep most recent per unique message) ---
    const crossChecks = dedupMissingDocResults(data.cross_check_results);

    const singleIssues = data.single_doc_issues;

    const filteredCrossChecks =
        filter === 'single_doc' ? [] : crossChecks;

    const filteredSingleIssues =
        filter === 'cross_check' ? [] : singleIssues;

    const filterButtons: { key: FilterType; label: string }[] = [
        { key: 'all', label: 'All' },
        { key: 'cross_check', label: 'Cross-Check' },
        { key: 'single_doc', label: 'Single Doc' },
    ];

    return (
        <div className="space-y-5">
            {/* Summary Bar */}
            <SummaryBar
                passed={data.summary.passed}
                failed={data.summary.failed}
                missingDocs={crossChecks.filter((r) => r.status === 'missing_document').length}
                skipped={data.summary.skipped}
                singleDocOpen={data.summary.single_doc_open ?? 0}
                lastRunAt={data.last_validation_at}
            />

            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
                {filterButtons.map((fb) => (
                    <button
                        key={fb.key}
                        onClick={() => setFilter(fb.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            filter === fb.key
                                ? 'bg-brand-600 text-white'
                                : 'bg-secondary/50 text-secondary hover:bg-secondary hover:text-primary'
                        }`}
                    >
                        {fb.label}
                    </button>
                ))}
            </div>

            {/* Cross-Check Results */}
            {filteredCrossChecks.length > 0 && (
                <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                        <span className="h-px flex-1 bg-secondary" />
                        <span>Cross-Check Results</span>
                        <span className="text-xs font-normal text-tertiary">({filteredCrossChecks.length})</span>
                        <span className="h-px flex-1 bg-secondary" />
                    </h3>
                    {filteredCrossChecks.map((r) => (
                        <CrossCheckCard key={r.result_id} result={r} />
                    ))}
                </div>
            )}

            {/* Single Document Issues */}
            {filteredSingleIssues.length > 0 && (
                <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                        <span className="h-px flex-1 bg-secondary" />
                        <span>Single Document Issues</span>
                        <span className="text-xs font-normal text-tertiary">({filteredSingleIssues.length})</span>
                        <span className="h-px flex-1 bg-secondary" />
                    </h3>
                    {filteredSingleIssues.map((issue) => (
                        <SingleDocIssueCard
                            key={issue.issue_id}
                            issue={issue}
                            containerId={containerId}
                            containerNumber={containerNumber}
                        />
                    ))}
                </div>
            )}

            {/* No results after filter */}
            {filteredCrossChecks.length === 0 && filteredSingleIssues.length === 0 && (
                <div className="text-center py-8 text-tertiary text-sm">
                    No results match the selected filter.
                </div>
            )}

            {/* AI Observations */}
            {data.ai_observations && (
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                        <span className="h-px flex-1 bg-secondary" />
                        <span>AI Observations</span>
                        <span className="h-px flex-1 bg-secondary" />
                    </h3>
                    <div className="rounded-lg bg-brand-50 ring-1 ring-brand-200 p-4">
                        <p className="text-sm text-brand-800 whitespace-pre-wrap">{data.ai_observations}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Hook to get failed validation count for badge display.
 * Pass enabled=false to defer the API call until needed.
 */
export function useValidationFailedCount(containerId: string | undefined, enabled = true) {
    const { data } = useContainerValidation(containerId, { enabled });
    if (!data) return 0;
    return data.summary.failed + (data.summary.single_doc_open ?? 0) + (data.summary.missing_required_docs ?? 0);
}
